import {
  BodyBox,
  Comment,
  CommentsSection,
  Text,
  Select,
  Icon,
  pxToRem,
  useDebounce,
} from '@lbcamden/lib-react';
import { DateArg, formatDMYWithTime, formatOrFallback } from '@lbcamden/shared-utils';
import { FunctionComponent, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import {
  useFullCase,
  usePerson,
  useProperty,
  useReferenceListSelectOptions,
} from '~/client/hooks/storeHooks';
import { getCaseStatus } from '~/client/utils/cases';
import { getPropertyBreadcrumbs, toCasePage, toPropertyPage } from '~/client/utils/navigation';
import { isTempNumericId } from '~/common/tinybase/utils';
import { CaseEventContactMethodCode, CaseEventInOrOutCode } from '~/common/types/cases';

import { useHandlePendingItem } from '../../../hooks/useHandlePendingItem';
import { InfoItem, InfoList, ItemDetails } from '../../common/ItemDetails';
import { PageTitle } from '../../common/PageTitle';
import { DirectoryUserInfo } from '../../shared/DirectoryUserInfo';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';
import { DropdownsContainer, EventInfo, EventsContainer, selectStyles } from './CasePage.styled';
import { useCloseCaseDialog } from './useCloseCaseDialog';
import { useCreateCaseEvent } from './useCreateCaseEvent';

const contactMethodInOutMap: Record<CaseEventContactMethodCode, CaseEventInOrOutCode> = {
  [CaseEventContactMethodCode.HomeVisit]: CaseEventInOrOutCode.Out, // Home visit - Out
  [CaseEventContactMethodCode.GeneralInfo]: CaseEventInOrOutCode.NotApplicable, // General info - NA
};

const pendingEventText = 'Pending';

const formatEventDate = (value: DateArg) =>
  value === pendingEventText ? value : formatDMYWithTime(value);

export const CasePage: FunctionComponent = () => {
  const { propertyId: propertyIdParam, caseId: caseIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);
  const caseId = parseInt(caseIdParam!);

  const property = useProperty(propertyId);
  const caseItem = useFullCase(caseId);
  const person = usePerson(caseItem?.personId ?? 0);
  const referenceListSelectOptions = useReferenceListSelectOptions();

  const { isPendingItem: isPendingCase } = useHandlePendingItem(
    caseItem?.caseId,
    toPropertyPage(propertyId),
    (realId) => toCasePage(propertyId, realId),
  );
  const createCaseEvent = useCreateCaseEvent(caseId);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [contactMethodCode, setContactMethodCode] = useState<CaseEventContactMethodCode>();
  const [inOrOutCode, setInOrOutCode] = useState<CaseEventInOrOutCode>();

  const { showCloseCaseDialog, closeCaseDialog } = useCloseCaseDialog({
    propertyId,
    caseId,
    personId: caseItem?.personId,
  });

  const submitDisabled = !contactMethodCode || !inOrOutCode;

  const caseEvents: Comment[] = useMemo(
    () =>
      caseItem?.events.map(({ eventId, contactMethod, inOrOut, note, createdBy, created }) => ({
        id: String(eventId),
        info: (
          <EventInfo>
            <DirectoryUserInfo camdenAccountName={createdBy} />
            <span>
              {contactMethod}
              {inOrOut !== 'NA' ? ` (${inOrOut.toLowerCase()})` : ''}
            </span>
          </EventInfo>
        ),
        text: note,
        created: isTempNumericId(eventId) ? pendingEventText : created,
      })) || [],
    [caseItem?.events],
  );

  const setDefaultInOrOut = () => {
    const defaultInOrOut = contactMethodCode ? contactMethodInOutMap[contactMethodCode] : undefined;

    setInOrOutCode(defaultInOrOut);
  };

  const onSubmitEvent = (note: string) =>
    void (!submitDisabled && createCaseEvent(contactMethodCode, inOrOutCode, note));

  const stringifyCodeValue = (value?: number) => (value === undefined ? value : String(value));

  useEffect(setDefaultInOrOut, [contactMethodCode]);

  useDebounce(
    () => {
      if (contactMethodCode && inOrOutCode) {
        textAreaRef.current?.focus();
      }
    },
    250,
    [contactMethodCode, inOrOutCode],
  );

  if (!property || !caseItem) {
    return <LoadInNotFoundPage />;
  }

  const { isClosed } = caseItem;
  const caseName = `Case ${caseId}`;
  const openCaseTitle = isPendingCase ? `${caseItem.demand} (pending)` : caseItem.demand;
  const title = isClosed ? `${caseItem.demand} (closed)` : openCaseTitle;

  return (
    <BodyBox>
      <PageTitle title={caseName} />
      <PageBreadcrumbs links={[...getPropertyBreadcrumbs(property), { name: caseName }]} />
      <ItemDetails
        titleLabel="Case"
        title={title}
        actions={
          isClosed
            ? undefined
            : [
                {
                  key: 'update',
                  disabled: isPendingCase,
                  children: (
                    <>
                      <Icon icon="ui/cross-circle" $color="primary" width={pxToRem(18)} />
                      Close case
                    </>
                  ),
                  onClick: showCloseCaseDialog,
                },
              ]
        }
        information={
          [
            ['Regarding', person?.fullName],
            ['Status', getCaseStatus(caseItem)],
            isClosed && ['Close reason', caseItem.closeReason],
            [
              'Reported',
              isPendingCase ? 'Pending' : formatDMYWithTime(caseItem.reported || caseItem.created),
            ],
            ['Updated', formatOrFallback(caseItem.updated, formatDMYWithTime)],
            // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
            ['Created by', <DirectoryUserInfo camdenAccountName={caseItem.createdBy} />],
            // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
            ['Lead officer', <DirectoryUserInfo camdenAccountName={caseItem.leadOfficer} />],
            [
              'Supporting officers',
              caseItem.supportingOfficers.length ? (
                // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
                <InfoList>
                  {caseItem.supportingOfficers.map((officer) => (
                    <li key={officer}>
                      <DirectoryUserInfo camdenAccountName={officer} />
                    </li>
                  ))}
                </InfoList>
              ) : undefined,
            ],
          ].filter(Boolean) as InfoItem[]
        }
      />
      {(!isClosed || !!caseEvents.length) && (
        <EventsContainer>
          <Text variant="h2">Events</Text>
          {!isClosed && (
            <DropdownsContainer>
              <Select
                css={selectStyles}
                aria-label="Contact method"
                className="contactMethod"
                placeholder="Select contact method"
                options={referenceListSelectOptions('LWP_EVENT')}
                value={stringifyCodeValue(contactMethodCode)}
                onChange={(value) => setContactMethodCode(parseInt(value))}
              />
              <Select
                css={selectStyles}
                aria-label="Contact in or out"
                className="inOrOut"
                placeholder="In or out?"
                options={referenceListSelectOptions('LWP_COMM')}
                value={stringifyCodeValue(inOrOutCode)}
                onChange={(value) => setInOrOutCode(parseInt(value))}
              />
            </DropdownsContainer>
          )}
          <CommentsSection
            textAreaRef={textAreaRef as RefObject<HTMLTextAreaElement>}
            comments={caseEvents}
            commentType="event"
            disabled={submitDisabled}
            readOnly={isClosed}
            formatDate={formatEventDate}
            onSubmitComment={onSubmitEvent}
          />
        </EventsContainer>
      )}
      {closeCaseDialog}
    </BodyBox>
  );
};
